/**
 * Leitura de timestamp vindo do backend, que chega **sem fuso**.
 *
 * O backend grava `DateTime.Now` e o valor volta do SQL Server como `datetime2`, um tipo que não
 * guarda offset. O JSON sai então como `"2026-08-11T20:57:00"` — sem `Z`, sem `-03:00` — e o
 * `new Date(...)` do navegador, seguindo a especificação, interpreta string sem fuso como horário
 * **local**. Em produção o servidor roda em UTC e quem lê está em BRT, então todo instante do
 * backend era lido 3 h adiantado.
 *
 * No feed isso não aparecia (o backend decide visibilidade comparando com o próprio relógio, em
 * `NewsVisibility`), mas na lista de quem publica sim: comunicado recém-publicado ficava marcado
 * como "agendado" por 3 h — a tela dizendo que a publicação não valeu quando ela já estava no ar.
 *
 * ⚠ A premissa é "instante sem fuso veio em UTC", que vale porque o App Service roda em UTC. Uma
 * API rodando localmente em BRT devolve BRT, e aí a leitura fica 3 h adiantada no ambiente de
 * desenvolvimento. A correção de raiz é o backend serializar com fuso — enquanto ela não vem, é
 * aqui que a premissa fica escrita, num lugar só, em vez de espalhada por cada `new Date()`.
 */
export const parseServerDate = (iso: string | null | undefined): Date | null => {
  if (!iso) return null;
  // Só completa o que está faltando: se o instante já declara fuso (`Z` ou `±hh:mm`), quem manda
  // é ele. Reinterpretar um valor explícito seria trocar um erro por outro.
  const temFuso = /(?:Z|[+-]\d{2}:?\d{2})$/.test(iso);
  const d = new Date(temFuso ? iso : `${iso}Z`);
  return isNaN(d.getTime()) ? null : d;
};

/**
 * Publicação marcada para o futuro — o post existe, está publicado, e ainda não entrou no feed.
 *
 * `agora` é parâmetro para o teste poder fixar o instante; a tela chama sem argumento.
 */
export const isScheduled = (publishedAt: string | null | undefined, agora: Date = new Date()) => {
  const d = parseServerDate(publishedAt);
  return d !== null && d > agora;
};
